/**
 * Sieve — the play-session state machine (§3), mode-agnostic.
 *
 * Pure and side-effect-free: given a normalised puzzle and a sequence of taps it
 * derives status, mistakes and outcome. Works for both dailies — the only
 * difference is what counts as a "target" cell (a prime, or a multiple of X).
 */

import { isPrime } from '../engine/primes';
import { isDivisible } from '../engine/divisibility';
import { MISTAKE_BUDGET, PRIME_COUNT } from '../engine/grid';
import type { GameState, Outcome, PlayPuzzle, TapResult } from './types';

/** Number of targets to find. Fixed at 5 in both modes (Multiples §3.2). */
export const TARGET_COUNT = PRIME_COUNT;

export function createGame(puzzle: PlayPuzzle): GameState {
  return { puzzle, taps: [], tapResults: [], found: 0, mistakes: 0, status: 'playing' };
}

/** True once the session has ended (won or lost). */
export function isOver(g: GameState): boolean {
  return g.status !== 'playing';
}

/**
 * Whether a cell is a target. `targetIndices` is authoritative (validated at
 * generation), but we cross-check against the engine as defence in depth against
 * a corrupted queue — the engine is the source of truth.
 */
export function isTargetCell(p: PlayPuzzle, index: number): boolean {
  if (!p.targetIndices.includes(index)) return false;
  const n = p.grid[index]!;
  return p.mode === 'multiples' ? isDivisible(n, p.divisor!) : isPrime(n);
}

/**
 * Applies a tap at `index`. No-ops if the game is over or the cell was already
 * committed. A tap is an immediate commit — there is no submit step (§6.2).
 */
export function applyTap(g: GameState, index: number): GameState {
  if (g.status !== 'playing') return g;
  if (index < 0 || index >= g.puzzle.grid.length) return g;
  if (g.taps.includes(index)) return g;

  const correct = isTargetCell(g.puzzle, index);
  const result: TapResult = correct ? 'correct' : 'wrong';
  g.taps.push(index);
  g.tapResults.push(result);

  if (correct) g.found += 1;
  else g.mistakes += 1;

  if (g.found >= TARGET_COUNT) g.status = 'won';
  else if (g.mistakes >= MISTAKE_BUDGET) g.status = 'lost';

  return g;
}

/** Rebuilds state by replaying a tap list — used to resume from storage (§9). */
export function replay(puzzle: PlayPuzzle, taps: readonly number[]): GameState {
  const g = createGame(puzzle);
  for (const t of taps) applyTap(g, t);
  return g;
}

/** Derives the outcome tier for a finished game (§3.1). */
export function outcomeOf(g: GameState): Outcome | null {
  if (g.status === 'won') return g.mistakes === 0 ? 'flawless' : 'solved';
  if (g.status === 'lost') return 'failed';
  return null;
}

/** Mistakes remaining before the game ends. */
export function mistakesRemaining(g: GameState): number {
  return Math.max(0, MISTAKE_BUDGET - g.mistakes);
}
